export class PitchDetector {
  private sampleRate: number;
  private noteFrequencies: Map<string, number>;
  private frequencyHistory: number[] = [];
  private readonly HISTORY_SIZE = 8; // Keep last 8 frequency readings for smoothing

  constructor(sampleRate: number) {
    this.sampleRate = sampleRate;
    this.noteFrequencies = this.generateNoteFrequencies();
  }

  private generateNoteFrequencies(): Map<string, number> {
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const frequencies = new Map<string, number>();
    
    // A4 = 440 Hz reference
    const A4_FREQUENCY = 440;
    const A4_INDEX = 57; // A4 is the 58th key on piano (0-indexed = 57)
    
    // Generate frequencies for multiple octaves (covering guitar/bass range)
    for (let octave = 0; octave <= 8; octave++) {
      for (let noteIndex = 0; noteIndex < 12; noteIndex++) {
        const midiNumber = octave * 12 + noteIndex;
        const frequency = A4_FREQUENCY * Math.pow(2, (midiNumber - A4_INDEX) / 12);
        
        // Only include audible range relevant for guitar/bass (extended for bass)
        if (frequency >= 30 && frequency <= 4000) {
          const noteName = notes[noteIndex];
          frequencies.set(`${noteName}${octave}`, frequency);
          
          // Also store without octave number for general matching
          frequencies.set(noteName, frequency);
        }
      }
    }
    
    return frequencies;
  }

  detectPitch(audioData: Float32Array, sensitivity: number = 1.0): number {
    // Apply pre-emphasis to boost higher frequencies (helps with acoustic guitar)
    const emphasized = this.preEmphasis(audioData);
    
    // Use autocorrelation for pitch detection
    const correlations = this.autoCorrelate(emphasized, sensitivity);
    const fundamentalFreq = this.findFundamentalFrequency(correlations, sensitivity);
    
    // Filter out noise - only return frequencies in musical range (extended for bass)
    if (fundamentalFreq < 30 || fundamentalFreq > 4000) {
      return 0;
    }
    
    // Add frequency to history for smoothing
    if (fundamentalFreq > 0) {
      this.addToFrequencyHistory(fundamentalFreq);
    }
    
    // Return smoothed frequency to reduce vibrato instability
    return this.getSmoothedFrequency();
  }

  private preEmphasis(buffer: Float32Array): Float32Array {
    const emphasized = new Float32Array(buffer.length);
    emphasized[0] = buffer[0];
    
    // Detect if this might be a bass signal by checking low-frequency content
    const rms = this.calculateRMS(buffer);
    let lowFreqContent = 0;
    
    // Simple low-frequency detection: check for slow variations
    for (let i = 10; i < buffer.length - 10; i++) {
      const variation = Math.abs(buffer[i] - buffer[i - 10]);
      lowFreqContent += variation;
    }
    lowFreqContent /= (buffer.length - 20);
    
    // Reduce pre-emphasis for bass-like signals (high RMS but low variation)
    const bassLikeSignal = rms > 0.05 && lowFreqContent < rms * 0.3;
    const preEmphasisCoeff = bassLikeSignal ? 0.75 : 0.95; // Less aggressive for bass
    
    // Apply adaptive pre-emphasis filter: y[n] = x[n] - coeff * x[n-1]
    for (let i = 1; i < buffer.length; i++) {
      emphasized[i] = buffer[i] - preEmphasisCoeff * buffer[i - 1];
    }
    
    return emphasized;
  }

  // Add method to calculate RMS for level meter
  calculateRMS(buffer: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i];
    }
    return Math.sqrt(sum / buffer.length);
  }

  // Frequency smoothing methods for vibrato handling
  private addToFrequencyHistory(frequency: number): void {
    this.frequencyHistory.push(frequency);
    if (this.frequencyHistory.length > this.HISTORY_SIZE) {
      this.frequencyHistory.shift();
    }
  }

  private getSmoothedFrequency(): number {
    if (this.frequencyHistory.length === 0) {
      return 0;
    }

    // Use median filtering to reduce vibrato spikes while preserving note changes
    const sortedHistory = [...this.frequencyHistory].sort((a, b) => a - b);
    const midIndex = Math.floor(sortedHistory.length / 2);
    
    if (sortedHistory.length % 2 === 0) {
      // Even number of samples - average the two middle values
      return (sortedHistory[midIndex - 1] + sortedHistory[midIndex]) / 2;
    } else {
      // Odd number of samples - return the middle value
      return sortedHistory[midIndex];
    }
  }

  // Clear frequency history when switching notes
  clearFrequencyHistory(): void {
    this.frequencyHistory = [];
  }

  // Get the confidence of the last pitch detection (for debugging)
  private lastConfidence = 0;
  
  getLastConfidence(): number {
    return this.lastConfidence;
  }

  private autoCorrelate(buffer: Float32Array, sensitivity: number = 1.0): Float32Array {
    const bufferSize = buffer.length;
    const correlations = new Float32Array(bufferSize);
    
    // Calculate RMS to filter out low-amplitude signals
    let rms = 0;
    for (let i = 0; i < bufferSize; i++) {
      rms += buffer[i] * buffer[i];
    }
    rms = Math.sqrt(rms / bufferSize);
    
    // Adjust RMS threshold based on sensitivity
    const rmsThreshold = 0.005 / sensitivity;
    if (rms < rmsThreshold) {
      return correlations;
    }
    
    // Autocorrelation calculation
    for (let lag = 0; lag < bufferSize; lag++) {
      let correlation = 0;
      for (let i = 0; i < bufferSize - lag; i++) {
        correlation += buffer[i] * buffer[i + lag];
      }
      correlations[lag] = correlation;
    }
    
    return correlations;
  }

  private findFundamentalFrequency(correlations: Float32Array, sensitivity: number = 1.0): number {
    const bufferSize = correlations.length;
    const sampleRate = this.sampleRate;
    
    // Normalize correlations by the first value to get relative correlation
    const normalizedCorrelations = new Float32Array(correlations.length);
    const firstCorrelation = correlations[0];
    if (firstCorrelation === 0) return 0;
    
    for (let i = 0; i < correlations.length; i++) {
      normalizedCorrelations[i] = correlations[i] / firstCorrelation;
    }
    
    // Define search range for musical frequencies (extended for bass)
    const minPeriod = Math.floor(sampleRate / 4000); // Highest frequency (4000 Hz)
    const maxPeriod = Math.floor(sampleRate / 30);   // Lowest frequency (30 Hz)
    
    // Look for the first significant peak after the initial peak
    let bestLag = 0;
    let bestCorrelation = 0;
    
    // Adaptive threshold based on signal strength
    // Increased minimum threshold to reduce background noise sensitivity
    let maxCorr = 0;
    const endIndex = Math.min(maxPeriod, bufferSize / 3);
    for (let i = minPeriod; i < endIndex; i++) {
      if (normalizedCorrelations[i] > maxCorr) {
        maxCorr = normalizedCorrelations[i];
      }
    }
    
    // Determine if we're likely looking for bass frequencies based on maxPeriod
    const isBassRange = maxPeriod > sampleRate / 200; // Looking for frequencies below 200Hz
    
    // Adjust correlation threshold based on sensitivity and frequency range
    // More lenient thresholds for bass frequencies to handle vibrato better
    const baseThreshold = isBassRange ? 
      Math.max(0.15, Math.min(0.4, maxCorr * 0.5)) : // Even lower base threshold for bass
      Math.max(0.2, Math.min(0.5, maxCorr * 0.6));   // Original threshold for higher frequencies
    
    let threshold = baseThreshold / Math.sqrt(sensitivity); // Lower threshold for higher sensitivity
    
    for (let lag = minPeriod; lag < Math.min(maxPeriod, bufferSize / 3); lag++) {
      const correlation = normalizedCorrelations[lag];
      
      // Look for peaks (higher than neighbors and above adaptive threshold)
      if (correlation > threshold && 
          correlation > bestCorrelation &&
          lag > minPeriod) {
        
        // Check if it's a local peak (optional for weak signals)
        const isLocalPeak = (lag === minPeriod || correlation > normalizedCorrelations[lag - 1]) &&
                           (lag >= bufferSize / 3 - 1 || correlation > normalizedCorrelations[lag + 1]);
        
        // For weaker signals, don't require strict local peaks
        if (isLocalPeak || threshold < 0.25) {
          bestCorrelation = correlation;
          bestLag = lag;
        }
      }
    }
    
    // Store confidence for debugging
    this.lastConfidence = bestCorrelation;
    
    // Adjust final threshold based on sensitivity and frequency range (more lenient for bass)
    const baseFinalThreshold = isBassRange ?
      Math.max(0.12, threshold * 0.7) :  // Even more lenient for bass frequencies
      Math.max(0.15, threshold * 0.8);   // Original threshold for higher frequencies
    const finalThreshold = baseFinalThreshold / Math.sqrt(sensitivity);
    if (bestLag === 0 || bestCorrelation < finalThreshold) {
      return 0;
    }
    
    // Parabolic interpolation for better frequency precision
    const y1 = normalizedCorrelations[bestLag - 1];
    const y2 = normalizedCorrelations[bestLag];
    const y3 = normalizedCorrelations[bestLag + 1];
    
    const a = (y1 - 2 * y2 + y3) / 2;
    const b = (y3 - y1) / 2;
    
    let refinedLag = bestLag;
    if (a !== 0) {
      const peak = -b / (2 * a);
      if (Math.abs(peak) < 1) {
        refinedLag = bestLag + peak;
      }
    }
    
    return sampleRate / refinedLag;
  }

  frequencyToNote(frequency: number): string | null {
    if (frequency <= 0) return null;
    
    // Use equal temperament to find the closest note
    // A4 = 440Hz is our reference (MIDI note 69)
    const A4_FREQUENCY = 440;
    const A4_MIDI = 69;
    
    // Calculate MIDI note number
    const midiNote = 12 * Math.log2(frequency / A4_FREQUENCY) + A4_MIDI;
    const roundedMidi = Math.round(midiNote);
    
    // Calculate how far off we are in cents
    const cents = 1200 * (midiNote - roundedMidi);
    
    // Adaptive tolerance: more lenient for bass frequencies (below 200Hz)
    // Bass guitars naturally have more vibrato and tuning instability
    const isBassFrequency = frequency < 200;
    const tolerance = isBassFrequency ? 60 : 40; // ±60 cents for bass, ±40 cents for higher frequencies
    
    if (Math.abs(cents) > tolerance) {
      return null;
    }
    
    // Convert MIDI note to note name
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const noteIndex = roundedMidi % 12;
    
    return noteNames[noteIndex];
  }

  private frequencyToCents(freq1: number, freq2: number): number {
    return 1200 * Math.log2(freq1 / freq2);
  }

  // Get all available notes for the game
  getAvailableNotes(): string[] {
    return ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'];
  }
}